import { formatTimeString } from './timeUtils';

export type PreferredTimeRangeOption = {
  code: string;
  text: string;
  helpText: string;
  start?: string;
  end?: string;
};

const MIN_SLOT_MINUTES = 15;
const EVENING_FALLBACK_MINUTES = 60;

const STANDARD_PERIOD_RANGES: Record<string, { start: string; end: string }> = {
  morning: { start: '08:00', end: '12:00' },
  afternoon: { start: '12:00', end: '17:00' },
  evening: { start: '17:00', end: '20:00' },
};

/**
 * "Evening" must never label a slot that starts before this time. When the
 * work day ends too early for a real evening window (e.g. work ends at
 * 3:30 PM → the old fallback showed "Evening 2:30 PM – 3:30 PM" overlapping
 * Afternoon), the Evening option is dropped instead.
 */
const EVENING_START_MINUTES = parseHHmmToMinutes(STANDARD_PERIOD_RANGES.evening.start) as number;

export function isFlexiblePreferredTimeCode(code: string): boolean {
  if (!code) return false;
  const u = code.toUpperCase();
  if (u === 'ANY' || u === 'FLEX') return true;
  const lower = code.toLowerCase();
  return (
    lower === 'anytime' ||
    lower === 'flexible' ||
    lower === 'any time' ||
    lower === 'any time works'
  );
}

/**
 * Public booking form persists preferred-time choices as lowercase strings
 * ("morning", "afternoon", "evening", "anytime") on `Request.preferredTimeRange`.
 * Internal admin UIs work with master-data options keyed by code (MRN/AFT/EVE/ANY)
 * and display text (Morning/Afternoon/...). Use this helper to resolve any of
 * those representations (code, display text, or lowercase alias) to the actual
 * option from the provided list so downstream comparisons that use
 * `option.code === preferredTime || option.text === preferredTime` keep working
 * and don't silently fall back to a different time-of-day.
 *
 * Returns `null` when no compatible option is present in `options` (e.g. the
 * matching window was clipped out by company hours).
 */
const STANDARD_ALIAS_TO_CODES: Record<string, string[]> = {
  morning: ['MRN'],
  afternoon: ['AFT'],
  evening: ['EVE'],
  anytime: ['ANY', 'FLEX'],
  any: ['ANY', 'FLEX'],
  flexible: ['ANY', 'FLEX'],
  'any time': ['ANY', 'FLEX'],
  'any time works': ['ANY', 'FLEX'],
};

export function resolvePreferredTimeOption(
  value: string | null | undefined,
  options: PreferredTimeRangeOption[] | null | undefined
): PreferredTimeRangeOption | null {
  if (!value || !options || options.length === 0) return null;
  const target = value.trim().toLowerCase();
  if (!target) return null;

  for (const opt of options) {
    if (
      (opt.code || '').toLowerCase() === target ||
      (opt.text || '').toLowerCase() === target
    ) {
      return opt;
    }
  }

  const candidateCodes = STANDARD_ALIAS_TO_CODES[target];
  if (candidateCodes) {
    for (const opt of options) {
      if (candidateCodes.includes((opt.code || '').toUpperCase())) return opt;
    }
  }

  return null;
}

/**
 * True when the option appears to represent a flexible / any-time choice based
 * on its code, text, or helpText. Broader than `isFlexiblePreferredTimeCode`
 * so tenants that use non-standard codes ("ANYTIME", "FLEXIBLE", etc.) still
 * get the special flexible handling during clipping.
 */
function looksFlexible(option: PreferredTimeRangeOption): boolean {
  if (isFlexiblePreferredTimeCode(option.code)) return true;
  const haystack = `${option.text || ''} ${option.helpText || ''}`.toLowerCase();
  return /\b(any\s*time|anytime|flexible|flex)\b/.test(haystack);
}

export function parseHHmmToMinutes(t: string | null | undefined): number | null {
  if (t == null || typeof t !== 'string') return null;
  const cleaned = t.trim();
  const match = cleaned.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

export function minutesToHHmm(m: number): string {
  const clamped = Math.max(0, Math.min(m, 24 * 60 - 1));
  const h = Math.floor(clamped / 60);
  const min = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function formatRangeLabel(startMin: number, endMin: number): string {
  return `${formatTimeString(minutesToHHmm(startMin))} – ${formatTimeString(minutesToHHmm(endMin))}`;
}

function textShowsClockTimes(text: string | null | undefined): boolean {
  if (!text) return false;
  return /\d{1,2}(?::\d{2})?\s*(AM|PM)/i.test(text);
}

/**
 * Extracts a 24-hour HH:MM start/end pair from free-form text such as
 * "8:00 AM - 11:00 AM", "12 PM – 5 PM", or "08:00am-04:00pm". Returns null
 * when fewer than two clock times are present.
 */
function tryExtractStartEndFromText(
  text: string | null | undefined
): { start: string; end: string } | null {
  if (!text) return null;
  const re = /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/gi;
  const hits: { hour: number; minute: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    let hour = parseInt(m[1], 10);
    const minute = m[2] ? parseInt(m[2], 10) : 0;
    const period = m[3].toUpperCase();
    if (!Number.isFinite(hour) || hour < 0 || hour > 23) continue;
    if (period === 'PM' && hour < 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    hits.push({ hour, minute });
    if (hits.length === 2) break;
  }
  if (hits.length < 2) return null;
  const start = `${String(hits[0].hour).padStart(2, '0')}:${String(hits[0].minute).padStart(2, '0')}`;
  const end = `${String(hits[1].hour).padStart(2, '0')}:${String(hits[1].minute).padStart(2, '0')}`;
  return { start, end };
}

/**
 * Looks for a known time-of-day name (Morning, Afternoon, Evening) in the
 * option's code/text/helpText and returns the matching standard window. Used
 * as a last-resort fallback when neither structured start/end fields nor
 * parseable clock times are present on the option.
 */
function inferStandardPeriodRange(
  option: PreferredTimeRangeOption
): { start: string; end: string } | null {
  const code = option.code?.toUpperCase() || '';
  if (code === 'MRN') return STANDARD_PERIOD_RANGES.morning;
  if (code === 'AFT') return STANDARD_PERIOD_RANGES.afternoon;
  if (code === 'EVE') return STANDARD_PERIOD_RANGES.evening;

  const haystack = `${option.code || ''} ${option.text || ''} ${option.helpText || ''}`.toLowerCase();
  if (/\bmorning\b/.test(haystack)) return STANDARD_PERIOD_RANGES.morning;
  if (/\bafternoon\b/.test(haystack)) return STANDARD_PERIOD_RANGES.afternoon;
  if (/\bevening\b/.test(haystack)) return STANDARD_PERIOD_RANGES.evening;
  return null;
}

/**
 * Resolves the option's start/end window in minutes-since-midnight, trying
 * (in order): explicit start/end fields, clock times embedded in text,
 * clock times embedded in helpText, and finally the standard window for a
 * known period name. Returns null when no usable window can be derived.
 */
function resolveOptionRange(
  option: PreferredTimeRangeOption
): { startMin: number; endMin: number } | null {
  const tryPair = (start: string | null | undefined, end: string | null | undefined) => {
    const s = parseHHmmToMinutes(start);
    const e = parseHHmmToMinutes(end);
    if (s !== null && e !== null && e > s) return { startMin: s, endMin: e };
    return null;
  };

  const direct = tryPair(option.start, option.end);
  if (direct) return direct;

  const fromText = tryExtractStartEndFromText(option.text);
  if (fromText) {
    const r = tryPair(fromText.start, fromText.end);
    if (r) return r;
  }

  const fromHelp = tryExtractStartEndFromText(option.helpText);
  if (fromHelp) {
    const r = tryPair(fromHelp.start, fromHelp.end);
    if (r) return r;
  }

  const inferred = inferStandardPeriodRange(option);
  if (inferred) {
    const r = tryPair(inferred.start, inferred.end);
    if (r) return r;
  }

  return null;
}

/**
 * Picks a human-readable label (e.g. "Morning") for the option, preferring
 * the field that looks most like a name rather than a clock-time range. We
 * search text → helpText → fall back to empty so callers can substitute the
 * formatted range.
 */
function pickPeriodLabel(option: PreferredTimeRangeOption): string {
  const text = (option.text || '').trim();
  if (text && !textShowsClockTimes(text)) return text;

  const helpText = (option.helpText || '').trim();
  if (helpText && !textShowsClockTimes(helpText)) return helpText;

  return '';
}

function adjustOneOption(
  option: PreferredTimeRangeOption,
  workStart: number,
  workEnd: number
): PreferredTimeRangeOption | null {
  if (looksFlexible(option)) {
    const rangeLabel = formatRangeLabel(workStart, workEnd);
    const periodLabel = pickPeriodLabel(option) || 'Flexible';
    return {
      ...option,
      text: periodLabel,
      helpText: rangeLabel,
      start: minutesToHHmm(workStart),
      end: minutesToHHmm(workEnd),
    };
  }

  const resolved = resolveOptionRange(option);
  if (!resolved) {
    return { ...option };
  }

  const oStart = resolved.startMin;
  const oEnd = resolved.endMin;

  let cStart = Math.max(oStart, workStart);
  let cEnd = Math.min(oEnd, workEnd);
  const evening = isEveningOption(option.code, option.text, oStart);

  if (cEnd - cStart < MIN_SLOT_MINUTES && evening) {
    // Offer a shortened end-of-day window, but never let "Evening" label a
    // slot that starts before the real evening. When the day ends too early
    // the window collapses and the option is dropped below.
    cStart = Math.max(workStart, workEnd - EVENING_FALLBACK_MINUTES, EVENING_START_MINUTES);
    cEnd = workEnd;
  }

  if (cEnd - cStart < MIN_SLOT_MINUTES) {
    return null;
  }

  const rangeLabel = formatRangeLabel(cStart, cEnd);
  const periodLabel = pickPeriodLabel(option) || rangeLabel;
  return {
    ...option,
    text: periodLabel,
    helpText: rangeLabel,
    start: minutesToHHmm(cStart),
    end: minutesToHHmm(cEnd),
  };
}

function isEveningOption(code: string, text: string, optionStartMin: number): boolean {
  const u = code?.toUpperCase() || '';
  if (u === 'EVE') return true;
  if (/\bevening\b/i.test(text || '')) return true;
  return optionStartMin >= 17 * 60;
}

/**
 * Filters out time ranges whose end time has already passed when the selected date is today.
 * For future dates, returns all options unchanged. Flexible/ANYTIME options are always kept.
 */
export function filterPastTimeRanges(
  options: PreferredTimeRangeOption[],
  selectedDate: string | Date | null | undefined,
  nowOverride?: Date
): PreferredTimeRangeOption[] {
  if (!selectedDate) return options;

  const now = nowOverride ?? new Date();
  const selected = typeof selectedDate === 'string'
    ? new Date(selectedDate + 'T00:00:00')
    : selectedDate;

  if (
    selected.getFullYear() !== now.getFullYear() ||
    selected.getMonth() !== now.getMonth() ||
    selected.getDate() !== now.getDate()
  ) {
    return options;
  }

  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const out: PreferredTimeRangeOption[] = [];
  for (const option of options) {
    if (looksFlexible(option)) {
      out.push(option);
      continue;
    }

    const endMin = parseHHmmToMinutes(option.end);
    if (endMin === null) {
      out.push(option);
      continue;
    }

    if (endMin <= nowMinutes) {
      continue;
    }

    out.push(option);
  }
  return out;
}

/**
 * Default time-of-day preferences used when a tenant's master data is missing
 * or unusable. Mirrors the public booking form (`SchedulingStep`) so the
 * internal booking modal stays in lockstep with what the public flow shows.
 */
export const DEFAULT_PREFERRED_TIME_RANGES: PreferredTimeRangeOption[] = [
  { code: 'MRN', text: 'Morning', helpText: 'Morning', start: '08:00', end: '12:00' },
  { code: 'AFT', text: 'Afternoon', helpText: 'Afternoon', start: '12:00', end: '17:00' },
  { code: 'EVE', text: 'Evening', helpText: 'Evening', start: '17:00', end: '20:00' },
  { code: 'ANY', text: 'Flexible', helpText: 'Any time works', start: '08:00', end: '20:00' },
];

/**
 * Clips master preferred-time ranges to the booking form's first/last appointment window.
 * If both appointment bounds are missing, returns a shallow copy of the original list (no clipping).
 *
 * The clipping is robust to incomplete master data: when an option doesn't carry explicit
 * `start`/`end` fields, we'll try to derive them from clock times in the option's text or
 * helpText, and finally from a recognised period name (Morning / Afternoon / Evening).
 * Options that fall completely outside the form's hours are dropped.
 */
export function clipPreferredTimeRangesToCompanyHours(
  options: PreferredTimeRangeOption[],
  firstAppointmentTime?: string | null,
  lastAppointmentTime?: string | null
): PreferredTimeRangeOption[] {
  const hasFirst = firstAppointmentTime != null && String(firstAppointmentTime).trim() !== '';
  const hasLast = lastAppointmentTime != null && String(lastAppointmentTime).trim() !== '';

  if (!hasFirst && !hasLast) {
    return options.map(o => ({ ...o }));
  }

  const workStartParsed = hasFirst ? parseHHmmToMinutes(firstAppointmentTime) : 0;
  const workEndParsed = hasLast ? parseHHmmToMinutes(lastAppointmentTime) : 24 * 60 - 1;

  if (workStartParsed === null || workEndParsed === null || workEndParsed <= workStartParsed) {
    return options.map(o => ({ ...o }));
  }

  const workStart = workStartParsed;
  const workEnd = workEndParsed;

  const out: PreferredTimeRangeOption[] = [];
  for (const option of options) {
    const adjusted = adjustOneOption(option, workStart, workEnd);
    if (adjusted) {
      out.push(adjusted);
    }
  }
  return out;
}

/**
 * Returns a clipped, public-form-equivalent set of preferred-time options for
 * a booking form. Prefers the tenant's master data (when at least one
 * non-flexible window survives clipping) and falls back to the standard
 * Morning / Afternoon / Evening / Anytime preferences clipped to the same
 * window. This guarantees the internal booking modal always shows the same
 * choices the public booking form would for the selected booking form.
 */
export function getPreferredTimeRangesForBookingForm(
  masterOptions: PreferredTimeRangeOption[],
  firstAppointmentTime?: string | null,
  lastAppointmentTime?: string | null
): PreferredTimeRangeOption[] {
  const clippedMaster = clipPreferredTimeRangesToCompanyHours(
    masterOptions,
    firstAppointmentTime,
    lastAppointmentTime
  );
  const hasUsableMaster = clippedMaster.some(opt => !looksFlexible(opt));
  if (hasUsableMaster) return clippedMaster;

  return clipPreferredTimeRangesToCompanyHours(
    DEFAULT_PREFERRED_TIME_RANGES,
    firstAppointmentTime,
    lastAppointmentTime
  );
}

/** Public booking form scheduling step preference ids (lowercase strings). */
export type SchedulingTimePreferenceId = 'morning' | 'afternoon' | 'evening' | 'anytime';

export type SchedulingTimePreferenceOption = {
  id: SchedulingTimePreferenceId;
  label: string;
  description: string;
  recommended?: boolean;
  start?: string;
  end?: string;
};

const PREFERENCE_CODE_TO_ID: Record<string, SchedulingTimePreferenceId> = {
  MRN: 'morning',
  AFT: 'afternoon',
  EVE: 'evening',
  ANY: 'anytime',
  FLEX: 'anytime',
};

/**
 * Normalizes backend time strings (e.g. `"09:00:00"`, `"9:00 AM"`) to `HH:mm`
 * for clipping and display. Returns null when the value cannot be parsed.
 */
export function normalizeAppointmentTimeString(
  time: string | null | undefined
): string | null {
  if (time == null) return null;
  const trimmed = String(time).trim();
  if (!trimmed) return null;

  const hhmm = parseHHmmToMinutes(trimmed);
  if (hhmm !== null) {
    return minutesToHHmm(hhmm);
  }

  const fromText = tryExtractStartEndFromText(trimmed);
  if (fromText) {
    const mins = parseHHmmToMinutes(fromText.start);
    if (mins !== null) return minutesToHHmm(mins);
  }

  return null;
}

type PublicBookingHoursSource = {
  firstAppointmentTime?: string | null;
  lastAppointmentTime?: string | null;
  dailySchedules?: ReadonlyArray<DailyScheduleLike> | null;
};

/**
 * Reads first/last appointment bounds from public booking-form settings,
 * including PascalCase JSON fallbacks and optional per-day work schedules
 * when the API provides them.
 */
export function resolvePublicBookingFormAppointmentHours(
  settings: PublicBookingHoursSource | null | undefined,
  selectedDate?: string | Date | null
): { firstAppointmentTime: string | null; lastAppointmentTime: string | null } {
  const raw = settings as Record<string, unknown> | null | undefined;

  const firstRaw =
    settings?.firstAppointmentTime ?? (raw?.FirstAppointmentTime as string | undefined);
  const lastRaw =
    settings?.lastAppointmentTime ?? (raw?.LastAppointmentTime as string | undefined);

  const formFirst = normalizeAppointmentTimeString(firstRaw);
  const formLast = normalizeAppointmentTimeString(lastRaw);

  const dayHours = getCompanyHoursForDate(settings?.dailySchedules, selectedDate);
  const intersected = intersectHourRanges(
    { start: formFirst, end: formLast },
    dayHours
  );

  return {
    firstAppointmentTime: intersected.start ?? formFirst,
    lastAppointmentTime: intersected.end ?? formLast,
  };
}

function preferredOptionToSchedulingId(
  option: PreferredTimeRangeOption
): SchedulingTimePreferenceId | null {
  const code = (option.code || '').toUpperCase();
  if (PREFERENCE_CODE_TO_ID[code]) return PREFERENCE_CODE_TO_ID[code];

  const haystack = `${option.code || ''} ${option.text || ''} ${option.helpText || ''}`.toLowerCase();
  if (/\bmorning\b/.test(haystack)) return 'morning';
  if (/\bafternoon\b/.test(haystack)) return 'afternoon';
  if (/\bevening\b/.test(haystack)) return 'evening';
  if (looksFlexible(option)) return 'anytime';
  return null;
}

/**
 * Builds Morning / Afternoon / Evening / Flexible options for the public
 * booking form scheduling step, clipped to the form's first/last appointment
 * window (and optional company hours for the selected date).
 */
export function buildSchedulingTimePreferenceOptions(
  firstAppointmentTime?: string | null,
  lastAppointmentTime?: string | null,
  selectedDate?: string | Date | null
): SchedulingTimePreferenceOption[] {
  const first = normalizeAppointmentTimeString(firstAppointmentTime);
  const last = normalizeAppointmentTimeString(lastAppointmentTime);

  const clipped = filterPastTimeRanges(
    getPreferredTimeRangesForBookingForm(DEFAULT_PREFERRED_TIME_RANGES, first, last),
    selectedDate
  );

  const out: SchedulingTimePreferenceOption[] = [];
  for (const option of clipped) {
    const id = preferredOptionToSchedulingId(option);
    if (!id) continue;
    out.push({
      id,
      label: option.text,
      description: option.helpText,
      recommended: id === 'anytime',
      start: option.start,
      end: option.end,
    });
  }
  return out;
}

/**
 * Structural shape of a single day in `CompanyWorkScheduleDTO.dailySchedules`.
 * Declared here so this util stays decoupled from the generated DTOs and can
 * be shared with the React Native app without a circular import.
 */
export interface DailyScheduleLike {
  dayOfWeek?: string;
  startTime?: string | null;
  endTime?: string | null;
  isWorkDay?: boolean | null;
}

const DAY_OF_WEEK_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Company schedule entry resolved for a concrete date. Distinguishes a
 * configured non-work day (`isWorkDay: false`) from "no schedule data",
 * which {@link getCompanyDayScheduleForDate} signals by returning `null`.
 */
export interface CompanyDaySchedule {
  isWorkDay: boolean;
  start: string | null;
  end: string | null;
}

/**
 * Returns the company's schedule entry for the day-of-week that `date`
 * falls on. Returns `null` only when there is genuinely no data to consult
 * (schedule missing, unparseable date, or no entry for that weekday) so
 * callers can tell "closed that day" apart from "schedule not loaded".
 *
 * Time strings from the backend can include seconds (e.g. `"08:00:00"`);
 * downstream helpers (`parseHHmmToMinutes`) only consume the `HH:MM` prefix
 * so we pass the raw value through unchanged.
 */
export function getCompanyDayScheduleForDate(
  dailySchedules: ReadonlyArray<DailyScheduleLike> | null | undefined,
  date: string | Date | null | undefined
): CompanyDaySchedule | null {
  if (!dailySchedules || dailySchedules.length === 0) return null;
  if (!date) return null;

  const parsed = typeof date === 'string'
    ? new Date(date + 'T00:00:00')
    : date;
  if (isNaN(parsed.getTime())) return null;

  const dayName = DAY_OF_WEEK_NAMES[parsed.getDay()];
  if (!dayName) return null;

  const match = dailySchedules.find(d => d.dayOfWeek === dayName);
  if (!match) return null;

  return {
    isWorkDay: match.isWorkDay !== false,
    start: match.startTime ?? null,
    end: match.endTime ?? null,
  };
}

/**
 * Returns the company's configured work hours for the day-of-week that
 * `date` falls on. Returns `null` when the schedule is missing, the date is
 * unparseable, no entry exists for that weekday, the entry is marked as a
 * non-work day, or start/end aren't both populated.
 */
export function getCompanyHoursForDate(
  dailySchedules: ReadonlyArray<DailyScheduleLike> | null | undefined,
  date: string | Date | null | undefined
): { start: string; end: string } | null {
  const day = getCompanyDayScheduleForDate(dailySchedules, date);
  if (!day || !day.isWorkDay) return null;
  if (!day.start || !day.end) return null;
  return { start: day.start, end: day.end };
}

/**
 * Longest contiguous company work window in the weekly schedule. Used as a
 * fallback cap for exact-time pickers when the selected date isn't a work
 * day (or hours aren't loaded for it) so the UI never offers a 24-hour
 * range that would violate work-hour rules.
 */
export function getLargestWorkHourWindow(
  dailySchedules: ReadonlyArray<DailyScheduleLike> | null | undefined
): { start: string; end: string } | null {
  if (!dailySchedules || dailySchedules.length === 0) return null;

  let best: { start: string; end: string; duration: number } | null = null;
  for (const day of dailySchedules) {
    if (day.isWorkDay === false) continue;
    const startMin = parseHHmmToMinutes(day.startTime);
    const endMin = parseHHmmToMinutes(day.endTime);
    if (startMin == null || endMin == null || endMin <= startMin) continue;
    const duration = endMin - startMin;
    if (!best || duration > best.duration) {
      best = {
        start: minutesToHHmm(startMin),
        end: minutesToHHmm(endMin),
        duration,
      };
    }
  }
  return best ? { start: best.start, end: best.end } : null;
}

/**
 * Structural shape of an assignee's effective working day (per-date override →
 * recurring hours → company schedule), as `AssigneeWorkWindowsManager` maps it
 * from the backend. Times are tenant-local `HH:mm`.
 */
export interface AssigneeWorkDayLike {
  isWorking: boolean;
  windows: ReadonlyArray<{ start: string; end: string }>;
}

/**
 * The envelope (earliest start → latest end) of an assignee's working windows,
 * or `null` when they aren't working or no window parses.
 */
function getAssigneeEnvelope(
  assigneeDay: AssigneeWorkDayLike | null | undefined
): { start: string; end: string } | null {
  if (!assigneeDay || !assigneeDay.isWorking) return null;
  let startMin: number | null = null;
  let endMin: number | null = null;
  for (const w of assigneeDay.windows) {
    const s = parseHHmmToMinutes(w.start);
    const e = parseHHmmToMinutes(w.end);
    if (s == null || e == null || e <= s) continue;
    startMin = startMin == null ? s : Math.min(startMin, s);
    endMin = endMin == null ? e : Math.max(endMin, e);
  }
  if (startMin == null || endMin == null) return null;
  return { start: minutesToHHmm(startMin), end: minutesToHHmm(endMin) };
}

/**
 * Min/max times for exact-time start/end pickers.
 *
 * When the assignee's working day is known (`assigneeDay`) and they work,
 * the window is the envelope of their hours for that date — so a per-date
 * availability override that extends the day widens the picker (ClickUp
 * 86bcj7m6e). Otherwise prefers that day's company work hours so Saturday
 * 9–2 doesn't inherit weekday 8–5. If the date isn't a work day (or has no
 * hours), falls back to the largest work-hour window in the weekly schedule.
 * Optional `extraClip` (booking-form first/last appointment times) tightens
 * the company-hours window without ever widening it past work hours; it is
 * not applied when the assignee's hours are known.
 */
export function getExactTimePickerBounds(
  dailySchedules: ReadonlyArray<DailyScheduleLike> | null | undefined,
  date: string | Date | null | undefined,
  extraClip?: { start?: string | null; end?: string | null } | null,
  assigneeDay?: AssigneeWorkDayLike | null
): { minTime?: string; maxTime?: string } {
  // The assignee's own hours win over the booking-form clip: an operator
  // placing an exact time for a member who extended their day must reach the
  // extended hours even when the form's last-appointment time is earlier
  // (ClickUp 86bcj7m6e). The form clip only bounds customer-facing slots.
  const assigneeWindow = getAssigneeEnvelope(assigneeDay);
  if (assigneeWindow) {
    return { minTime: assigneeWindow.start, maxTime: assigneeWindow.end };
  }

  const companyWindow = getCompanyHoursForDate(dailySchedules, date)
    ?? getLargestWorkHourWindow(dailySchedules);

  if (companyWindow && extraClip) {
    const clipped = intersectHourRanges(companyWindow, extraClip);
    if (clipped.start && clipped.end) {
      return { minTime: clipped.start, maxTime: clipped.end };
    }
  }

  if (companyWindow) {
    return { minTime: companyWindow.start, maxTime: companyWindow.end };
  }

  if (extraClip?.start || extraClip?.end) {
    const startMin = parseHHmmToMinutes(extraClip.start);
    const endMin = parseHHmmToMinutes(extraClip.end);
    return {
      minTime: startMin != null ? minutesToHHmm(startMin) : undefined,
      maxTime: endMin != null ? minutesToHHmm(endMin) : undefined,
    };
  }

  return {};
}

/** Why a picked exact window falls outside the assignee's working hours. */
export type AssigneeHoursViolation =
  | { kind: 'non-work-day' }
  | { kind: 'outside-window'; windows: ReadonlyArray<{ start: string; end: string }> };

/**
 * Checks a picked `HH:mm` start/end against the assignee's working day. Both
 * ends must fall inside one window (mirrors the backend's `fitsAnyWindow`);
 * an end before the start (a wrap past midnight) counts as outside. Returns
 * `null` when the window fits or either time doesn't parse.
 */
export function getAssigneeHoursViolation(
  assigneeDay: AssigneeWorkDayLike,
  startHHmm: string | null | undefined,
  endHHmm: string | null | undefined
): AssigneeHoursViolation | null {
  if (!assigneeDay.isWorking) return { kind: 'non-work-day' };

  const startMin = parseHHmmToMinutes(startHHmm);
  const endMin = parseHHmmToMinutes(endHHmm);
  if (startMin == null || endMin == null) return null;

  const fits = endMin >= startMin && assigneeDay.windows.some(w => {
    const ws = parseHHmmToMinutes(w.start);
    const we = parseHHmmToMinutes(w.end);
    return ws != null && we != null && startMin >= ws && endMin <= we;
  });
  return fits ? null : { kind: 'outside-window', windows: assigneeDay.windows };
}

/**
 * Clamps an HH:MM value into `[minTime, maxTime]` when those bounds parse.
 * Returns the original string when the input isn't a time.
 */
export function clampHHmmToRange(
  time: string | null | undefined,
  minTime?: string | null,
  maxTime?: string | null
): string {
  if (!time) return time ?? '';
  const mins = parseHHmmToMinutes(time);
  if (mins == null) return time;
  const minM = parseHHmmToMinutes(minTime);
  const maxM = parseHHmmToMinutes(maxTime);
  if (minM != null && mins < minM) return minutesToHHmm(minM);
  if (maxM != null && mins > maxM) return minutesToHHmm(maxM);
  return minutesToHHmm(mins);
}

/**
 * Intersects two HH:MM time windows, picking the tighter bound on each side.
 * Either side can have missing bounds; in that case the present bound wins.
 * When the resulting window collapses (end <= start) both sides come back
 * null so the caller knows to skip clipping rather than show an empty
 * range.
 */
export function intersectHourRanges(
  a: { start?: string | null; end?: string | null } | null | undefined,
  b: { start?: string | null; end?: string | null } | null | undefined
): { start: string | null; end: string | null } {
  const aStart = parseHHmmToMinutes(a?.start);
  const aEnd = parseHHmmToMinutes(a?.end);
  const bStart = parseHHmmToMinutes(b?.start);
  const bEnd = parseHHmmToMinutes(b?.end);

  let start: number | null;
  if (aStart != null && bStart != null) start = Math.max(aStart, bStart);
  else start = aStart ?? bStart ?? null;

  let end: number | null;
  if (aEnd != null && bEnd != null) end = Math.min(aEnd, bEnd);
  else end = aEnd ?? bEnd ?? null;

  if (start != null && end != null && end <= start) {
    return { start: null, end: null };
  }

  return {
    start: start != null ? minutesToHHmm(start) : null,
    end: end != null ? minutesToHHmm(end) : null,
  };
}
